import type { FC } from 'react'
import { MarkdownMessage } from '@tunasong/ui-lib'
import { ToolUseChip } from './tool-use-chip.js'
import type { AgentTurnStream } from './use-agent-turn-stream.js'
import styles from './live-agent-response.module.css'

export interface LiveAgentResponseProps {
  stream: AgentTurnStream
  /** Optional accent color for the tool-use chips — passed through from
   *  the agent context so chips feel "owned" by the running agent. */
  accentColor?: string
  /** Re-submit the failed turn. When absent, the error renders without a Retry button. */
  onRetry?: () => void
  /** Cancel the in-flight turn. When absent, no Stop button renders. */
  onStop?: () => void
  /** Lifecycle of an in-flight cancel request — drives the Stop button's disabled/failed states. */
  stopState?: 'idle' | 'stopping' | 'failed'
}

/**
 * Synthetic feed item rendered while an agent stream is in flight. Sits at
 * the tail of the chat thread and shows:
 *  - the streamed text body, rendered as markdown via {@link MarkdownMessage}
 *  - a blinking caret while `pending` (respects `prefers-reduced-motion`)
 *  - one {@link ToolUseChip} per tool call, in invocation order
 *
 * The outer wrapper carries `aria-live="polite"` so screen readers announce
 * streamed updates without interrupting the user. `data-request-id` exposes
 * the cache key so end-to-end tests (and the future flicker-free transition)
 * can match the synthetic item against the persisted comment that replaces it.
 */
export const LiveAgentResponse: FC<LiveAgentResponseProps> = props => {
  const { stream, accentColor, onRetry, onStop, stopState } = props
  const { content, toolCalls, pending, error, requestId } = stream

  return (
    <div className={styles.container} aria-live="polite" data-request-id={requestId} data-testid="live-agent-response">
      <div className={styles.body}>
        <MarkdownMessage content={content} className={pending ? styles.markdownStreaming : undefined} />
        {pending ? (
          <span className={styles.caret} data-testid="live-caret" aria-hidden="true">
            ▍
          </span>
        ) : null}
      </div>

      {pending && onStop ? (
        <div className={styles.stopRow}>
          <button type="button" onClick={onStop} disabled={stopState === 'stopping'}>
            Stop
          </button>
          {stopState === 'failed' ? <span role="status">Couldn&apos;t stop — the reply will land.</span> : null}
        </div>
      ) : null}

      {toolCalls.length > 0 ? (
        <div className={styles.chips} role="group" aria-label="Tool calls">
          {toolCalls.map(toolCall => (
            <ToolUseChip key={toolCall.id} toolCall={toolCall} accentColor={accentColor} />
          ))}
        </div>
      ) : null}

      {error ? (
        <div className={styles.errorBox} role="alert">
          {error}{' '}
          {onRetry ? (
            <button type="button" onClick={onRetry}>
              Retry
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
